import z from 'zod'
import { DateRangeNumberSchema, UTCSecondsSchema } from '../time'
import { RoleIdSchema } from '../role'
import { LatLngSchema } from '../locations/latLng'
import {
  AccountStatusIdSchema,
  CompanyIdSchema,
  idSchemaV2,
  isId,
  TeamIdSchema,
  UserIdSchema,
  WorkflowActionIdSchema,
  WorkflowIdSchema,
  WorkflowStageIdSchema,
} from '../ids'
import { DispositionIdSchema } from '../disposition'
import { CustomFieldFilterSchema } from '../customFieldMap'
import { AuditPropsSchema } from '../common'
import { AreaIdSchema } from '../area'
import { ACCOUNT_EXPORT_FIELDS_DESCRIPTION } from './accountFields'

export const EXPORT_ENTITIES = ['CalendarEvent', 'Account', 'User', 'Task', 'Activity'] as const
export const EXPORT_ENTITIES_LABEL: Record<ExportType, string> = {
  CalendarEvent: 'Calendar Event',
  Account: 'Accounts',
  User: 'User',
  Task: 'Task',
  Activity: 'Activity',
} as const

export const ExportTypeSchema = z.enum(EXPORT_ENTITIES).meta({ description: 'Supported entities for export' })
export type ExportType = z.infer<typeof ExportTypeSchema>

export const ExportIdSchema = idSchemaV2('Export')
export type ExportId = z.infer<typeof ExportIdSchema>
export const isExportId = isId(ExportIdSchema)

export const SUPPORTED_EXPORT_FORMATS = ['json', 'csv', 'jsonl'] as const
export type ExportFormat = (typeof SUPPORTED_EXPORT_FORMATS)[number]

export const SUPPORTED_COMPRESSION_METHODS = ['gzip', 'none'] as const

export const ExportFilterSchema = z.object({
  areaIds: z.array(AreaIdSchema).optional().meta({ description: 'Filter account export to the specified areas' }),
  areaCoordinates: z
    .array(z.array(LatLngSchema).min(3))
    .optional()
    .meta({ description: 'Filter account export to accounts within these area boundaries' }),
  workflowId: WorkflowIdSchema.optional().meta({ description: 'Filter accounts by the specified workflow ID' }),
  teamIds: z
    .array(TeamIdSchema)
    .optional()
    .meta({ description: 'Filter exported data to these teams (set by downline scope)' }),
  userIds: z
    .array(UserIdSchema)
    .optional()
    .meta({ description: 'Filter exported activity/contact data to these users' }),
  workflowActionIds: z
    .array(WorkflowActionIdSchema)
    .optional()
    .meta({ description: 'Filter workflow activity export to these action IDs' }),
  cities: z.array(z.string()).optional().meta({ description: 'Filter workflow activity export to these cities' }),
  states: z.array(z.string()).optional().meta({ description: 'Filter workflow activity export to these states' }),
  zipCodes: z.array(z.string()).optional().meta({ description: 'Filter workflow activity export to these ZIP codes' }),
  dispositionIds: z
    .array(DispositionIdSchema)
    .optional()
    .meta({ description: 'Filter legacy contact activity export to these disposition IDs' }),
  statusIds: z.array(AccountStatusIdSchema).optional().meta({ description: 'Filter account export to these statuses' }),
  stageIds: z
    .array(WorkflowStageIdSchema)
    .optional()
    .meta({ description: 'Filter account export to these workflow stages' }),
  searchByCurrentStage: z.boolean().optional().meta({
    description:
      'Whether to filter account export to only accounts currently in the selected stages, rather than accounts that ever passed through them. The default value is true',
  }),
  roleIds: z
    .array(RoleIdSchema)
    .optional()
    .meta({ description: 'Filter exported users, or account owners/closers, to these roles' }),
  coordinates: z
    .array(LatLngSchema)
    .optional()
    .meta({ description: 'Filter account export to accounts within this polygon' }),
  advancedCustomFields: CustomFieldFilterSchema.optional().meta({
    description: 'Filter account export to these custom field values',
  }),
})
export type ExportFilter = z.infer<typeof ExportFilterSchema>

export const UnsavedExportSchema = z.object({
  name: z.string().max(256).optional().meta({ description: 'Optional user-defined name for the export' }),
  type: ExportTypeSchema,
  format: z
    .enum(SUPPORTED_EXPORT_FORMATS)
    .default('json')
    .meta({
      description:
        'Format of the export files. `json` writes each file as one JSON array of records. ' +
        '`jsonl` (JSON Lines) writes one JSON object per line with no surrounding array, which is easier to stream. ' +
        '`csv` writes a header row followed by one row per record.',
    }),
  compression: z.enum(SUPPORTED_COMPRESSION_METHODS).default('none').optional().meta({
    description: 'Compression method for the export file',
  }),
  range: DateRangeNumberSchema.optional().meta({
    description: 'Optional date range for the export. If not provided, the entire data set will be exported',
  }),
  filter: ExportFilterSchema,
  fields: z.array(z.string()).optional().meta({ description: ACCOUNT_EXPORT_FIELDS_DESCRIPTION }),
})
export type UnsavedExport = z.infer<typeof UnsavedExportSchema>

export const ExportStatusSchema = z.enum(['New', 'Processing', 'Completed', 'Failed']).default('New').meta({
  description: 'Export status: `New` (queued), `Processing`, `Completed` (files ready for /export/download) or `Failed`',
})
export type ExportStatus = z.infer<typeof ExportStatusSchema>

export const ExportDataSchema = UnsavedExportSchema.extend({
  ...AuditPropsSchema.shape,
  exportId: ExportIdSchema,
  companyId: CompanyIdSchema.meta({ description: 'ID of the company for which the export is created' }),
  ownerId: UserIdSchema.optional().meta({ description: 'User who created the export' }),
  total: z.number().meta({ description: 'Total number of records included in the export' }),
  processed: z.number().meta({
    description: 'Number of records processed so far in the export',
  }),
  expiresAt: UTCSecondsSchema.optional().meta({
    description: 'Timestamp when the export will expire and be deleted',
  }),
  status: ExportStatusSchema,
})
export type ExportData = z.infer<typeof ExportDataSchema>

export type TinyExportData = Pick<
  ExportData,
  'exportId' | 'companyId' | 'format' | 'range' | 'compression' | 'filter' | 'name' | 'fields'
>
